import { Controller } from '@hotwired/stimulus';
import Swal from 'sweetalert2';

export default class extends Controller<HTMLFormElement> {

    static values = {
        title: String,
        text: String,
        confirmButtonText: String,
        cancelButtonText: String,
        url: String,
        method: { type: String, default: 'POST' },
        sectionUrl: String,
        sectionTarget: String,
    };

    declare readonly titleValue: string;
    declare readonly textValue: string;
    declare readonly confirmButtonTextValue: string;
    declare readonly cancelButtonTextValue: string;
    declare readonly urlValue: string;
    declare readonly methodValue: string;
    declare readonly sectionUrlValue: string;
    declare readonly sectionTargetValue: string;
    declare readonly hasUrlValue: boolean;
    declare readonly hasSectionUrlValue: boolean;
    declare readonly hasSectionTargetValue: boolean;

    connect(): void {

        this.element.addEventListener(
            'submit',
            this.handleSubmit
        );
    }

    disconnect(): void {

        this.element.removeEventListener(
            'submit',
            this.handleSubmit
        );
    }

    private handleSubmit = async (
        event: Event
    ): Promise<void> => {

        event.preventDefault();

        const result = await Swal.fire({

            title:
                this.titleValue ||
                '¿Estás seguro?',

            text:
                this.textValue ||
                'Esta acción no se puede deshacer.',

            icon: 'warning',

            showCancelButton: true,

            confirmButtonText:
                this.confirmButtonTextValue ||
                'Sí, eliminar',

            cancelButtonText:
                this.cancelButtonTextValue ||
                'Cancelar',

            reverseButtons: true,

            focusCancel: true,
        });

        if (result.isConfirmed) {
            this.element.submit();
        }
    };

    async ask(event: Event): Promise<void> {
        event.preventDefault();

        const trigger = event.currentTarget as HTMLButtonElement | null;
        const url = trigger?.dataset.confirmUrlParam ?? (this.hasUrlValue ? this.urlValue : '');
        const method = trigger?.dataset.confirmMethodParam ?? this.methodValue ?? 'POST';
        const sectionUrl = trigger?.dataset.confirmSectionUrlParam ?? (this.hasSectionUrlValue ? this.sectionUrlValue : '');
        const sectionTarget = trigger?.dataset.confirmSectionTargetParam ?? (this.hasSectionTargetValue ? this.sectionTargetValue : '');

        const result = await this.confirm();
        if (!result.isConfirmed) return;

        if (!url) return;

        trigger?.setAttribute('disabled', 'disabled');

        try {
            const response = await fetch(url, {
                method,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!response.ok) {
                throw new Error(`Unexpected response status ${response.status}`);
            }

            if (sectionUrl && sectionTarget) {
                await this.reloadSection(sectionUrl, sectionTarget);
            }
        } catch (error) {
            console.error('[confirm] action error:', error);
            trigger?.removeAttribute('disabled');
            await Swal.fire({
                title: 'Error',
                text: 'No se pudo completar la acción.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    }

    private confirm(): Promise<Awaited<ReturnType<typeof Swal.fire>>> {
        return Swal.fire({
            title:
                this.titleValue ||
                '¿Estás seguro?',

            text:
                this.textValue ||
                'Esta acción no se puede deshacer.',

            icon: 'warning',

            showCancelButton: true,

            confirmButtonText:
                this.confirmButtonTextValue ||
                'Sí, eliminar',

            cancelButtonText:
                this.cancelButtonTextValue ||
                'Cancelar',

            reverseButtons: true,

            focusCancel: true,
        });
    }

    private async reloadSection(url: string, selector: string): Promise<void> {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const html = await response.text();
        const target = document.querySelector(selector);
        if (target) target.outerHTML = html;
    }
}
